

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    FormControlLabel,
    Checkbox,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewGuideAllocationModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        departure_date_id: '',
        trip_id: '',
        guide_id: '',
        travel_allowance: '',
        accommodation_required: false,
        meal_inclusion: false,
        flight_inclusion: false,
        trip_option_id: '',
        status: 'Pending',
        rejection_reason: '',
        whatsapp_group_link: '',
        trip_compensation: '',
        travel_expense: false
    });

    const [formErrors, setFormErrors] = useState({});
    const [departureDates, setDepartureDates] = useState([]);
    const [trips, setTrips] = useState([]);
    const [guides, setGuides] = useState([]);
    const [tripOptions, setTripOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
    }, [isOpen]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [departureDatesRes, tripsRes, guidesRes, tripOptionsRes] = await Promise.all([
                axiosInstance.get('/departure-dates/'),
                axiosInstance.get('/trips/'),
                axiosInstance.get('/accounts/?account_type=Guide'),
                axiosInstance.get('/trip-options/')
            ]);
            setDepartureDates(departureDatesRes.data);
            setTrips(tripsRes.data);
            setGuides(guidesRes.data);
            setTripOptions(tripOptionsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.departure_date_id) errors.departure_date_id = 'Departure Date is required';
        if (!formData.trip_id) errors.trip_id = 'Trip is required';
        if (!formData.guide_id) errors.guide_id = 'Guide is required';
        if (!formData.trip_option_id) errors.trip_option_id = 'Trip Option is required';
        if (!formData.trip_compensation) errors.trip_compensation = 'Trip Compensation is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                departure_date_id: parseInt(formData.departure_date_id),
                trip_id: parseInt(formData.trip_id),
                guide_id: parseInt(formData.guide_id),
                trip_option_id: parseInt(formData.trip_option_id),
                travel_allowance: formData.travel_allowance ? parseFloat(formData.travel_allowance) : null,
                trip_compensation: parseFloat(formData.trip_compensation),
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '', departure_date_id: '', trip_id: '', guide_id: '',
                travel_allowance: '', accommodation_required: false, meal_inclusion: false,
                flight_inclusion: false, trip_option_id: '', status: 'Pending',
                rejection_reason: '', whatsapp_group_link: '', trip_compensation: '', travel_expense: false
            });
        } catch (error) {
            console.error('Error saving Guide Allocation:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Guide Allocation',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const filteredDepartureDates = formData.trip_id 
        ? departureDates.filter(dd => dd.trip_id === parseInt(formData.trip_id))
        : departureDates;

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '800px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Guide Allocation</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.trip_id} required>
                                        <InputLabel>Trip</InputLabel>
                                        <Select
                                            name="trip_id"
                                            value={formData.trip_id}
                                            onChange={handleInputChange}
                                            label="Trip"

                                        >
                                            <MenuItem value=""><em>Select a Trip</em></MenuItem>
                                            {trips.map(t => (
                                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.departure_date_id} required>
                                        <InputLabel>Departure Date</InputLabel>
                                        <Select
                                            name="departure_date_id"
                                            value={formData.departure_date_id}
                                            onChange={handleInputChange}
                                            label="Departure Date"

                                            disabled={!formData.trip_id}
                                        >
                                            <MenuItem value=""><em>Select Departure Date</em></MenuItem>
                                            {filteredDepartureDates.map(dd => (
                                                <MenuItem key={dd.id} value={dd.id}>
                                                    {dd.departure_date ? new Date(dd.departure_date).toLocaleDateString() : dd.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.departure_date_id && <MDTypography variant="caption" color="error">{formErrors.departure_date_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.guide_id} required>
                                        <InputLabel>Guide</InputLabel>
                                        <Select
                                            name="guide_id"
                                            value={formData.guide_id}
                                            onChange={handleInputChange}
                                            label="Guide"

                                        >
                                            <MenuItem value=""><em>Select a Guide</em></MenuItem>
                                            {guides.map(g => (
                                                <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.trip_option_id} required>
                                        <InputLabel>Trip Option</InputLabel>
                                        <Select
                                            name="trip_option_id"
                                            value={formData.trip_option_id}
                                            onChange={handleInputChange}
                                            label="Trip Option"

                                        >
                                            <MenuItem value=""><em>Select Trip Option</em></MenuItem>
                                            {tripOptions.map(to => (
                                                <MenuItem key={to.id} value={to.id}>{to.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Trip Compensation"
                                        name="trip_compensation"
                                        type="number"
                                        value={formData.trip_compensation}
                                        onChange={handleInputChange}
                                        error={!!formErrors.trip_compensation}
                                        helperText={formErrors.trip_compensation}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Travel Allowance"
                                        name="travel_allowance"
                                        type="number"
                                        value={formData.travel_allowance}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"

                                        >
                                            <MenuItem value="Pending">Pending</MenuItem>
                                            <MenuItem value="Approved">Approved</MenuItem>
                                            <MenuItem value="Rejected">Rejected</MenuItem>
                                            <MenuItem value="Completed">Completed</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="WhatsApp Group Link"
                                        name="whatsapp_group_link"
                                        value={formData.whatsapp_group_link}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Rejection Reason"
                                        name="rejection_reason"
                                        value={formData.rejection_reason}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <MDBox mt={1}>
                                        <FormControlLabel
                                            control={<Checkbox checked={formData.accommodation_required} name="accommodation_required" onChange={handleInputChange} />}
                                            label="Accommodation Required"
                                        />
                                    </MDBox>
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <MDBox mt={1}>
                                        <FormControlLabel
                                            control={<Checkbox checked={formData.meal_inclusion} name="meal_inclusion" onChange={handleInputChange} />}
                                            label="Meal Inclusion"
                                        />
                                    </MDBox>
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <MDBox mt={1}>
                                        <FormControlLabel
                                            control={<Checkbox checked={formData.flight_inclusion} name="flight_inclusion" onChange={handleInputChange} />}
                                            label="Flight Inclusion"
                                        />
                                    </MDBox>
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <MDBox mt={1}>
                                        <FormControlLabel
                                            control={<Checkbox checked={formData.travel_expense} name="travel_expense" onChange={handleInputChange} />}
                                            label="Travel Expense"
                                        />
                                    </MDBox>
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving || loading}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Guide Allocation'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewGuideAllocationModal;

